import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Backup as BackupIcon,
  Restore as RestoreIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';

const AdminConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [systemInfo, setSystemInfo] = useState({});
  
  // 对话框状态
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openTagDialog, setOpenTagDialog] = useState(false);
  const [openSystemInfoDialog, setOpenSystemInfoDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTag, setSelectedTag] = useState(null);
  
  // 表单数据
  const [categoryFormData, setCategoryFormData] = useState({
    id: '',
    name: '',
    description: '',
    icon: '',
    sort_order: 0
  });
  
  const [tagFormData, setTagFormData] = useState({
    id: '',
    name: '',
    description: '',
    color: '#000000'
  });
  
  const [systemInfoFormData, setSystemInfoFormData] = useState({
    platform_name: '',
    platform_version: '',
    description: '',
    data_directory: '',
    media_base_url: '',
    supported_video_formats: '',
    supported_image_formats: ''
  });

  const fetchConfig = async () => {
    try {
      const response = await fetch('/api/all-config');
      if (response.ok) {
        const data = await response.json();
        setConfig(data);
        setCategories(data.categories || []);
        setTags(data.tags || []);
        setSystemInfo(data.system_info || {});
      }
    } catch (error) {
      console.error('获取配置失败:', error);
      showSnackbar('获取配置失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // 分类管理
  const handleCategoryAdd = () => {
    setSelectedCategory(null);
    setCategoryFormData({
      id: '',
      name: '',
      description: '',
      icon: '',
      sort_order: categories.length + 1
    });
    setOpenCategoryDialog(true);
  };

  const handleCategoryEdit = (category) => {
    setSelectedCategory(category);
    setCategoryFormData(category);
    setOpenCategoryDialog(true);
  };

  const handleCategoryDelete = async (categoryId) => {
    if (window.confirm('确定要删除该分类吗？')) {
      try {
        const response = await fetch(`/api/all-config/categories/${categoryId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showSnackbar('分类删除成功');
          fetchConfig();
        } else {
          showSnackbar('分类删除失败', 'error');
        }
      } catch (error) {
        console.error('删除分类失败:', error);
        showSnackbar('删除分类失败', 'error');
      }
    }
  };

  const handleCategorySave = async () => {
    try {
      const method = selectedCategory ? 'PUT' : 'POST';
      const url = selectedCategory 
        ? `/api/all-config/categories/${selectedCategory.id}`
        : '/api/all-config/categories';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryFormData)
      });
      
      if (response.ok) {
        showSnackbar(selectedCategory ? '分类更新成功' : '分类添加成功');
        setOpenCategoryDialog(false);
        fetchConfig();
      } else {
        showSnackbar(selectedCategory ? '分类更新失败' : '分类添加失败', 'error');
      }
    } catch (error) {
      console.error('保存分类失败:', error);
      showSnackbar('保存分类失败', 'error');
    }
  };

  // 标签管理
  const handleTagAdd = () => {
    setSelectedTag(null);
    setTagFormData({
      id: '',
      name: '',
      description: '',
      color: '#000000'
    });
    setOpenTagDialog(true);
  };

  const handleTagEdit = (tag) => {
    setSelectedTag(tag);
    setTagFormData(tag);
    setOpenTagDialog(true);
  };

  const handleTagDelete = async (tagId) => {
    if (window.confirm('确定要删除该标签吗？')) {
      try {
        const response = await fetch(`/api/all-config/tags/${tagId}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          showSnackbar('标签删除成功');
          fetchConfig();
        } else {
          showSnackbar('标签删除失败', 'error');
        }
      } catch (error) {
        console.error('删除标签失败:', error);
        showSnackbar('删除标签失败', 'error');
      }
    }
  };

  const handleTagSave = async () => {
    try {
      const method = selectedTag ? 'PUT' : 'POST';
      const url = selectedTag 
        ? `/api/all-config/tags/${selectedTag.id}`
        : '/api/all-config/tags';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tagFormData)
      });
      
      if (response.ok) {
        showSnackbar(selectedTag ? '标签更新成功' : '标签添加成功');
        setOpenTagDialog(false);
        fetchConfig();
      } else {
        showSnackbar(selectedTag ? '标签更新失败' : '标签添加失败', 'error');
      }
    } catch (error) {
      console.error('保存标签失败:', error);
      showSnackbar('保存标签失败', 'error');
    }
  };

  // 系统信息管理
  const handleSystemInfoEdit = () => {
    setSystemInfoFormData(systemInfo);
    setOpenSystemInfoDialog(true);
  };

  const handleSystemInfoSave = async () => {
    try {
      const response = await fetch('/api/all-config/system-info', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(systemInfoFormData)
      });
      
      if (response.ok) {
        showSnackbar('系统信息更新成功');
        setOpenSystemInfoDialog(false);
        fetchConfig();
      } else {
        showSnackbar('系统信息更新失败', 'error');
      }
    } catch (error) {
      console.error('保存系统信息失败:', error);
      showSnackbar('保存系统信息失败', 'error');
    }
  };

  // 配置管理操作
  const handleBackup = async () => {
    try {
      const response = await fetch('/api/all-config/backup', {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        showSnackbar(`配置备份成功，备份路径：${data.backup_path}`);
      } else {
        showSnackbar('配置备份失败', 'error');
      }
    } catch (error) {
      console.error('备份配置失败:', error);
      showSnackbar('备份配置失败', 'error');
    }
  };

  const handleValidate = async () => {
    try {
      const response = await fetch('/api/all-config/validate');
      if (response.ok) {
        const data = await response.json();
        if (data.valid) {
          showSnackbar('配置验证通过');
        } else {
          showSnackbar('配置验证失败', 'error');
        }
      }
    } catch (error) {
      console.error('验证配置失败:', error);
      showSnackbar('验证配置失败', 'error');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">

        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">配置操作</Typography>
            <Box>
              <Button 
                variant="contained" 
                startIcon={<BackupIcon />}
                onClick={handleBackup}
                sx={{ mr: 2 }}
              >
                备份配置
              </Button>
              <Button 
                variant="contained" 
                startIcon={<CheckCircleIcon />}
                onClick={handleValidate}
              >
                验证配置
              </Button>
            </Box>
          </Box>
          
          <Typography variant="body2" color="textSecondary">
            版本: {config.version} | 创建时间: {new Date(config.created_at).toLocaleString()} 
            | 更新时间: {new Date(config.updated_at).toLocaleString()}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            <Tab label="系统信息" icon={<SettingsIcon />} />
            <Tab label="分类管理" icon={<SettingsIcon />} />
            <Tab label="标签管理" icon={<SettingsIcon />} />
            <Tab label="验证规则" icon={<SettingsIcon />} />
            <Tab label="性能配置" icon={<SettingsIcon />} />
          </Tabs>

          <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>系统信息</Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button 
                variant="contained" 
                startIcon={<EditIcon />}
                onClick={handleSystemInfoEdit}
              >
                编辑系统信息
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell><strong>平台名称</strong></TableCell>
                    <TableCell>{systemInfo.platform_name}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>平台版本</strong></TableCell>
                    <TableCell>{systemInfo.platform_version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>描述</strong></TableCell>
                    <TableCell>{systemInfo.description}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>数据目录</strong></TableCell>
                    <TableCell>{systemInfo.data_directory}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>媒体基础URL</strong></TableCell>
                    <TableCell>{systemInfo.media_base_url}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>支持的视频格式</strong></TableCell>
                    <TableCell>{systemInfo.supported_video_formats?.join(', ')}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>支持的图片格式</strong></TableCell>
                    <TableCell>{systemInfo.supported_image_formats?.join(', ')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>分类管理</Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleCategoryAdd}
              >
                添加分类
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>名称</TableCell>
                    <TableCell>描述</TableCell>
                    <TableCell>图标</TableCell>
                    <TableCell>排序</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category.id}>
                      <TableCell>{category.id}</TableCell>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description}</TableCell>
                      <TableCell>{category.icon}</TableCell>
                      <TableCell>{category.sort_order}</TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          startIcon={<EditIcon />}
                          onClick={() => handleCategoryEdit(category)}
                          sx={{ mr: 1 }}
                        >
                          编辑
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DeleteIcon />}
                          onClick={() => handleCategoryDelete(category.id)}
                        >
                          删除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: activeTab === 2 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>标签管理</Typography>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleTagAdd}
              >
                添加标签
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>名称</TableCell>
                    <TableCell>描述</TableCell>
                    <TableCell>颜色</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.map((tag) => (
                    <TableRow key={tag.id}>
                      <TableCell>{tag.id}</TableCell>
                      <TableCell>{tag.name}</TableCell>
                      <TableCell>{tag.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={tag.color}
                          sx={{ 
                            bgcolor: tag.color, 
                            color: '#ffffff',
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="small" 
                          startIcon={<EditIcon />}
                          onClick={() => handleTagEdit(tag)}
                          sx={{ mr: 1 }}
                        >
                          编辑
                        </Button>
                        <Button 
                          size="small" 
                          startIcon={<DeleteIcon />}
                          onClick={() => handleTagDelete(tag.id)}
                        >
                          删除
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ display: activeTab === 3 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>验证规则</Typography>
            <Paper sx={{ p: 2 }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                {JSON.stringify(config.config.validation_rules, null, 2)}
              </pre>
            </Paper>
          </Box>

          <Box sx={{ display: activeTab === 4 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>性能配置</Typography>
            <Paper sx={{ p: 2 }}>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                {JSON.stringify(config.config.performance, null, 2)}
              </pre>
            </Paper>
          </Box>
        </Paper>

      {/* 分类对话框 */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedCategory ? '编辑分类' : '添加分类'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="ID"
            value={categoryFormData.id}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, id: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称"
            value={categoryFormData.name}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="描述"
            value={categoryFormData.description}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="图标"
            value={categoryFormData.icon}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, icon: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="排序"
            type="number"
            value={categoryFormData.sort_order}
            onChange={(e) => setCategoryFormData({ ...categoryFormData, sort_order: parseInt(e.target.value) })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>取消</Button>
          <Button 
            variant="contained" 
            onClick={handleCategorySave}
            disabled={!categoryFormData.id || !categoryFormData.name}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 标签对话框 */}
      <Dialog open={openTagDialog} onClose={() => setOpenTagDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{selectedTag ? '编辑标签' : '添加标签'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="ID"
            value={tagFormData.id}
            onChange={(e) => setTagFormData({ ...tagFormData, id: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="名称"
            value={tagFormData.name}
            onChange={(e) => setTagFormData({ ...tagFormData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="描述"
            value={tagFormData.description}
            onChange={(e) => setTagFormData({ ...tagFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="颜色"
            type="color"
            value={tagFormData.color}
            onChange={(e) => setTagFormData({ ...tagFormData, color: e.target.value })}
            margin="normal"
            InputProps={{
              startAdornment: <InputAdornment position="start">#</InputAdornment>,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTagDialog(false)}>取消</Button>
          <Button 
            variant="contained" 
            onClick={handleTagSave}
            disabled={!tagFormData.id || !tagFormData.name}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 系统信息对话框 */}
      <Dialog open={openSystemInfoDialog} onClose={() => setOpenSystemInfoDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>编辑系统信息</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="平台名称"
            value={systemInfoFormData.platform_name}
            onChange={(e) => setSystemInfoFormData({ ...systemInfoFormData, platform_name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="平台版本"
            value={systemInfoFormData.platform_version}
            onChange={(e) => setSystemInfoFormData({ ...systemInfoFormData, platform_version: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="描述"
            value={systemInfoFormData.description}
            onChange={(e) => setSystemInfoFormData({ ...systemInfoFormData, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="数据目录"
            value={systemInfoFormData.data_directory}
            onChange={(e) => setSystemInfoFormData({ ...systemInfoFormData, data_directory: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="媒体基础URL"
            value={systemInfoFormData.media_base_url}
            onChange={(e) => setSystemInfoFormData({ ...systemInfoFormData, media_base_url: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSystemInfoDialog(false)}>取消</Button>
          <Button 
            variant="contained" 
            onClick={handleSystemInfoSave}
            disabled={!systemInfoFormData.platform_name || !systemInfoFormData.platform_version}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 消息提示 */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminConfig;